import { ArrowDown, ArrowUp, Pencil, Plus, Sparkles, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import { Can } from '@nucleus-commerce/admin-kit/permissions';
import type { Attribute, AttributeValue } from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';
import { toast } from '@nucleus-commerce/ui/components/sonner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@nucleus-commerce/ui/components/table';

import { MediaThumb, useMedia } from '@/features/media';

import { byPosition, movedValueIds, withoutSkuCode } from '../api/attribute-mappers';
import { useReorderAttributeValues, useSuggestSkuCodes } from '../hooks/use-attribute-mutations';

import { AttributeValueDeleteDialog } from './attribute-value-delete-dialog';
import { AttributeValueDialog } from './attribute-value-dialog';

type Editing = { kind: 'new' } | { kind: 'edit'; value: AttributeValue } | null;

function SwatchImage({ mediaId, label }: { mediaId: string; label: string }) {
  const { data } = useMedia(mediaId);
  return (
    <span role="img" aria-label={label} className="block size-5">
      <MediaThumb media={data ?? null} className="border-border size-5 rounded-full border" />
    </span>
  );
}

/** The value's swatch: an image, a color, or a dashed ring for none. An image wins over a color. */
function Swatch({ value }: { value: AttributeValue }) {
  const { t } = useTranslation('attributes');
  const color = value.swatch?.color;
  const mediaId = value.swatch?.mediaId;
  if (mediaId) return <SwatchImage mediaId={mediaId} label={t('values.swatchImage')} />;
  if (!color) {
    return (
      <span
        role="img"
        aria-label={t('values.noSwatch')}
        className="border-border block size-5 rounded-full border border-dashed"
      />
    );
  }
  return (
    <span
      role="img"
      aria-label={t('values.swatch', { color })}
      className="border-border block size-5 rounded-full border"
      // The color is the merchant's data, not a theme color.
      style={{ backgroundColor: color }}
    />
  );
}

/**
 * Values of a select or multiselect attribute. Each change is saved at once (they are their own
 * endpoints), so this sits outside the settings form and the save bar.
 */
export function AttributeValuesEditor({ attribute }: { attribute: Attribute }) {
  const { t } = useTranslation('attributes');
  const reorder = useReorderAttributeValues();
  const suggest = useSuggestSkuCodes();
  const [editing, setEditing] = useState<Editing>(null);
  const [deleting, setDeleting] = useState<AttributeValue | null>(null);
  const values = useMemo(() => [...attribute.values].sort(byPosition), [attribute.values]);
  const missingCodes = withoutSkuCode(values).length;

  const move = (value: AttributeValue, offset: -1 | 1) => {
    const valueIds = movedValueIds(values, value.id, offset);
    if (valueIds.length > 0) reorder.mutate({ attributeId: attribute.id, valueIds });
  };

  const addButton = (
    <Can permission="attributes.write">
      <Button type="button" variant="outline" onClick={() => setEditing({ kind: 'new' })}>
        <Plus aria-hidden />
        {t('values.add')}
      </Button>
    </Can>
  );

  const suggestButton =
    missingCodes > 0 ? (
      <Can permission="attributes.write">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={suggest.isPending}
          onClick={() =>
            suggest.mutate(attribute.id, {
              onSuccess: ({ items }) =>
                toast.success(t('toasts.codesSuggested', { count: items.length })),
            })
          }
        >
          <Sparkles aria-hidden />
          {suggest.isPending ? t('values.suggesting') : t('values.suggestCodes')}
        </Button>
      </Can>
    ) : null;

  return (
    <FormSection title={t('values.title')} description={t('values.description')}>
      {values.length > 0 ? (
        <div className="-mt-2 flex flex-wrap items-center justify-between gap-2">
          <p className="text-muted-foreground text-sm" data-testid="values-without-code">
            {missingCodes > 0 ? t('values.withoutCode', { count: missingCodes }) : null}
          </p>
          {suggestButton}
        </div>
      ) : null}
      {values.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t('values.empty')}</p>
      ) : (
        <div className="-mx-6">
          <Table aria-label={t('values.list', { label: attribute.label })}>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12 pl-6">
                  <span className="sr-only">{t('valueFields.swatch')}</span>
                </TableHead>
                <TableHead>{t('valueFields.label')}</TableHead>
                <TableHead>{t('valueFields.code')}</TableHead>
                <TableHead>{t('valueFields.skuCode')}</TableHead>
                <TableHead className="pr-6 text-right">{t('columns.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {values.map((value, index) => (
                <TableRow key={value.id} aria-label={value.label}>
                  <TableCell className="pl-6">
                    <Swatch value={value} />
                  </TableCell>
                  <TableCell className="font-medium">{value.label}</TableCell>
                  <TableCell>
                    <code className="text-muted-foreground text-xs">{value.value}</code>
                  </TableCell>
                  <TableCell>
                    {value.skuCode ? (
                      <code className="text-[13px]">{value.skuCode}</code>
                    ) : (
                      <span className="text-muted-foreground text-xs">{t('values.noSkuCode')}</span>
                    )}
                  </TableCell>
                  <TableCell className="pr-6 text-right">
                    <Can permission="attributes.write">
                      <div className="flex justify-end gap-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          aria-label={t('values.moveUp', { label: value.label })}
                          disabled={index === 0 || reorder.isPending}
                          onClick={() => move(value, -1)}
                        >
                          <ArrowUp aria-hidden />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          aria-label={t('values.moveDown', { label: value.label })}
                          disabled={index === values.length - 1 || reorder.isPending}
                          onClick={() => move(value, 1)}
                        >
                          <ArrowDown aria-hidden />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          aria-label={t('values.edit', { label: value.label })}
                          onClick={() => setEditing({ kind: 'edit', value })}
                        >
                          <Pencil aria-hidden />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          className="text-destructive hover:text-destructive"
                          aria-label={t('values.delete', { label: value.label })}
                          onClick={() => setDeleting(value)}
                        >
                          <Trash2 aria-hidden />
                        </Button>
                      </div>
                    </Can>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <div>{addButton}</div>
      <AttributeValueDialog
        attribute={attribute}
        value={editing?.kind === 'edit' ? editing.value : null}
        open={editing !== null}
        onOpenChange={(open) => {
          if (!open) setEditing(null);
        }}
      />
      <AttributeValueDeleteDialog
        value={deleting}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
      />
    </FormSection>
  );
}
